<template>
  <aside
    class="h-full bg-stone-100 border-r border-stone-200 flex flex-col transition-all duration-300"
    :class="isOpen ? 'w-64' : 'w-0 overflow-hidden'"
  >
    <!-- Header with New Chat + Close button -->
    <div class="p-3 border-b border-stone-200">
      <div class="flex items-center gap-2">
        <button
          @click="$emit('newChat')"
          class="flex-1 flex items-center gap-2 px-3 py-2.5 bg-white hover:bg-stone-50 border border-stone-200 rounded-lg text-sm font-medium text-stone-700 transition"
        >
          <svg class="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
          </svg>
          Novy chat
        </button>
        <!-- Close button -->
        <button
          @click="$emit('close')"
          class="p-2 text-stone-400 hover:text-stone-600 hover:bg-stone-200 rounded-lg transition"
          title="Zavrit panel"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Search -->
    <div class="p-3 border-b border-stone-200">
      <div class="relative">
        <svg
          class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Hledat..."
          class="w-full pl-9 pr-3 py-2 bg-white border border-stone-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
        />
      </div>
    </div>

    <!-- Conversations List -->
    <div class="flex-1 overflow-y-auto">
      <!-- Recent Section -->
      <div v-if="filteredConversations.length > 0" class="py-2">
        <div class="px-3 py-1.5 text-xs font-medium text-stone-500 uppercase tracking-wide">
          Nedavne
        </div>
        <div class="space-y-0.5 px-2">
          <button
            v-for="conv in filteredConversations"
            :key="conv.id"
            @click="selectConversation(conv)"
            class="w-full flex items-center gap-2 px-2 py-2 rounded-lg text-left text-sm transition group"
            :class="conv.id === activeConversationId
              ? 'bg-stone-200/80 text-stone-900'
              : 'text-stone-600 hover:bg-stone-200/50 hover:text-stone-900'"
          >
            <svg class="w-4 h-4 flex-shrink-0 opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <span class="flex-1 truncate">{{ conv.title || 'Bez nazvu' }}</span>
            <button
              @click.stop="$emit('deleteConversation', conv.id)"
              class="p-1 text-stone-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition"
              title="Smazat"
            >
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </button>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="p-4 text-center">
        <svg class="w-10 h-10 mx-auto mb-2 text-stone-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
        <p v-if="searchQuery" class="text-sm text-stone-500">
          Zadne vysledky pro "{{ searchQuery }}"
        </p>
        <p v-else class="text-sm text-stone-500">
          Zadne konverzace
        </p>
      </div>
    </div>

  </aside>
</template>

<script setup>
import { ref, computed } from 'vue';

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: true
  },
  conversations: {
    type: Array,
    default: () => []
  },
  activeConversationId: {
    type: [String, Number],
    default: null
  }
});

const emit = defineEmits(['newChat', 'selectConversation', 'deleteConversation', 'close']);

const searchQuery = ref('');

const filteredConversations = computed(() => {
  if (!searchQuery.value) return props.conversations;

  const query = searchQuery.value.toLowerCase();
  return props.conversations.filter(conv =>
    (conv.title || '').toLowerCase().includes(query)
  );
});

function selectConversation(conv) {
  emit('selectConversation', conv);
}
</script>
